import React, { ComponentClass, FunctionComponent } from "react";

import { usePageTitle, UsePageTitleOptions } from "../hooks/usePageTitle";

type TitleResolver<P> = string | ((props: P) => string);

/**
 * Higher-Order Component that adds page title functionality to class components
 * @param titleOrResolver - Static title string or function that receives props and returns title
 * @param options - Optional configuration for the page title
 * @returns HOC function that wraps a component with page title functionality
 */
export const withPageTitle = <P extends object>(
  titleOrResolver: TitleResolver<P>,
  options: UsePageTitleOptions = {}
) => {
  return (WrappedComponent: ComponentClass<P>) => {
    const WithPageTitleComponent: FunctionComponent<P> = (props) => {
      // Resolve the title based on whether it"s a string or function
      const resolvedTitle = typeof titleOrResolver === "function"
        ? titleOrResolver(props)
        : titleOrResolver;

      // Use the hook to manage the page title
      usePageTitle(resolvedTitle, options);

      // Render the wrapped component with all props and ref
      return <WrappedComponent {...props} />;
    };

    // Set display name for debugging
    const componentName = WrappedComponent.displayName || WrappedComponent.name || "Component";
    WithPageTitleComponent.displayName = `withPageTitle(${componentName})`;

    return WithPageTitleComponent;
  };
};
